import { all, takeLatest, put, select, call } from 'redux-saga/effects'

import { apiError, showPopup } from '../helpers'
import { logout, LOGOUT } from './auth'
import { captureException } from '../helpers/Sentry'
import { getProducts, createTicket } from '../api'
import { selectActiveState, fetchStates } from './states'

const initialState = {
  isOpen: false,
  selectedPropertyId: '',
  products: [],
  selectedProductId: '',
  severity: 0,
  title: '',
  body: '',
  attachments: [],
  loading: false
}

export const moduleName = 'interactionForm'
export const TOGGLE_MODAL_REQUEST = `${moduleName}/TOGGLE_MODAL_REQUEST`
export const TOGGLE_MODAL_SUCCESS = `${moduleName}/TOGGLE_MODAL_SUCCESS`
export const CLEAR_MODAL = `${moduleName}/CLEAR_MODAL`

export const SET_PROPERTY = `${moduleName}/SET_PROPERTY`
export const SET_PRODUCT = `${moduleName}/SET_PRODUCT`
export const SET_SEVERITY = `${moduleName}/SET_SEVERITY`
export const SET_FORM_LOADING = `${moduleName}/SET_FORM_LOADING`
export const SET_INPUT_VALUE = `${moduleName}/SET_INPUT_VALUE`
export const ATTACH_FILES = `${moduleName}/ATTACH_FILES`
export const REMOVE_FILE = `${moduleName}/REMOVE_FILE`

export const FETCH_PRODUCTS_REQUEST = `${moduleName}/FETCH_PRODUCTS_REQUEST`
export const FETCH_PRODUCTS_SUCCESS = `${moduleName}/FETCH_PRODUCTS_SUCCESS`
export const FETCH_PRODUCTS_FAIL = `${moduleName}/FETCH_PRODUCTS_FAIL`

export const CREATE_TICKET_REQUEST = `${moduleName}/CREATE_TICKET_REQUEST`
export const CREATE_TICKET_SUCCESS = `${moduleName}/CREATE_TICKET_SUCCESS`
export const CREATE_TICKET_FAIL = `${moduleName}/CREATE_TICKET_FAIL`

export default function reducer(state = initialState, action) {
  const { type, payload } = action

  switch (type) {
    case TOGGLE_MODAL_SUCCESS:
      return { ...state, ...payload }

    case CLEAR_MODAL:
      return { ...initialState }

    case SET_PROPERTY:
      return { ...state, selectedPropertyId: payload, loading: true }

    case SET_PRODUCT:
      return { ...state, selectedProductId: payload }

    case SET_SEVERITY:
      return { ...state, severity: payload }

    case CREATE_TICKET_REQUEST:
      return { ...state, loading: true }

    case FETCH_PRODUCTS_SUCCESS:
      return { ...state, ...payload, loading: false }

    case ATTACH_FILES:
      return { ...state, attachments: [...state.attachments, ...payload] }

    case CREATE_TICKET_SUCCESS:
      return { ...initialState }

    case REMOVE_FILE: {
      const attachments = [
        ...state.attachments.slice(0, payload),
        ...state.attachments.slice(payload + 1)
      ]

      return { ...state, attachments }
    }

    case SET_INPUT_VALUE: {
      const { name, value } = payload

      return { ...state, [name]: value }
    }

    case LOGOUT:
      return { ...initialState }

    default:
      return state
  }
}

export function handleInput(name, value) {
  return {
    type: SET_INPUT_VALUE,
    payload: { name, value }
  }
}

export function toggleModal(isOpen = true) {
  if (isOpen) {
    return {
      type: TOGGLE_MODAL_REQUEST
    }
  } else {
    return {
      type: CLEAR_MODAL
    }
  }
}

export function setProperty(propertyId) {
  return {
    type: SET_PROPERTY,
    payload: propertyId
  }
}

export function setProduct(productId) {
  return {
    type: SET_PRODUCT,
    payload: productId
  }
}

export function setSeverity(payload) {
  return {
    type: SET_SEVERITY,
    payload
  }
}

export function attachFiles(files) {
  return {
    type: ATTACH_FILES,
    payload: files
  }
}

export function removeAttachment(id) {
  return {
    type: REMOVE_FILE,
    payload: id
  }
}

export function createNewTicket() {
  return {
    type: CREATE_TICKET_REQUEST
  }
}

export const fetchProductsSaga = function*(action) {
  try {
    const propertyId = yield select(
      (state) => state.interactionForm.selectedPropertyId
    )

    if (!propertyId) {
      return yield put({
        type: FETCH_PRODUCTS_SUCCESS,
        payload: {
          products: [],
          selectedProductId: ''
        }
      })
    }

    const response = yield call(getProducts, propertyId)

    const payload = {
      products: response
    }

    if (response.length === 1) {
      payload.selectedProductId = response[0].product_id
    }

    yield put({
      type: FETCH_PRODUCTS_SUCCESS,
      payload
    })
  } catch (e) {
    yield put(showPopup(e))
    if (e instanceof apiError) {
      const { code, message } = e
      yield put({
        type: FETCH_PRODUCTS_FAIL,
        payload: {
          code,
          message
        }
      })
      if (code === 2) {
        yield put(logout())
      }
    } else {
      yield put({
        type: FETCH_PRODUCTS_FAIL,
        payload: {
          code: 0,
          message: 'look what I got!'
        }
      })
      // TODO call some cross-screen error
      captureException(e)
    }
  }
}

export const createTicketSaga = function*() {
  try {
    const {
      selectedPropertyId,
      selectedProductId,
      title,
      body,
      severity,
      attachments
    } = yield select((state) => state.interactionForm)
    yield call(createTicket, {
      propertyId: selectedPropertyId,
      productId: selectedProductId,
      title,
      body,
      severity,
      attachments
    })

    yield put({
      type: CREATE_TICKET_SUCCESS
    })
    yield put(selectActiveState(0, false))
    yield put(fetchStates())
  } catch (e) {
    yield put(showPopup(e))
    if (e instanceof apiError) {
      const { code, message } = e
      yield put({
        type: CREATE_TICKET_FAIL,
        payload: {
          code,
          message
        }
      })
      if (code === 2) {
        yield put(logout())
      }
    } else {
      yield put({
        type: CREATE_TICKET_FAIL,
        payload: {
          code: 0,
          message: 'look what I got!'
        }
      })
      // TODO call some cross-screen error
      captureException(e)
    }
  }
}

export const toggleModalSaga = function*() {
  try {
    let { selectedPropertyId, entities: propertyEntities } = yield select(
      ({ properties }) => properties
    )

    const withInitialProperty = Boolean(selectedPropertyId)

    if (!withInitialProperty) {
      selectedPropertyId = propertyEntities[0].property_id
    }

    const { selectedProductId, entities } = yield select(
      ({ products }) => products
    )

    yield put({
      type: TOGGLE_MODAL_SUCCESS,
      payload: {
        isOpen: true,
        selectedPropertyId,
        products: entities,
        selectedProductId
      }
    })

    if (!withInitialProperty) {
      yield call(fetchProductsSaga)
    }
  } catch (e) {
    yield put(showPopup(e))
  }
}

export const saga = function*() {
  yield all([
    takeLatest(SET_PROPERTY, fetchProductsSaga),
    takeLatest(CREATE_TICKET_REQUEST, createTicketSaga),
    takeLatest(TOGGLE_MODAL_REQUEST, toggleModalSaga)
  ])
}
